import { PublicClientApplication, ProtocolMode, type Configuration } from '@azure/msal-browser';
import { fetchRuntimeConfig } from './runtime-config';
import { applyRuntimeFeatures } from './features';

// Vite-baked values are the LOCAL-DEV fallback only (EW-166): deployed
// environments get their auth config at runtime from /config.json, so the CI
// image stays neutral. initAuth() must complete before anything touches the
// exports below — main.tsx awaits it before the first render.
const envTenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';
const envClientId = import.meta.env.VITE_AUTH_ADMIN_CLIENT_ID ?? '';
// Generic OIDC issuer URL (EW-66). When set, MSAL runs in OIDC protocol mode against it
// (hand-written metadata skips the Microsoft cloud-instance discovery a non-Entra issuer
// would fail). Runtime-configurable since EW-66; the VITE_ value serves local dev.
const envAuthorityUrl = import.meta.env.VITE_AUTH_AUTHORITY_URL ?? '';

export let apiScope = '';
export let msalInstance!: PublicClientApplication;
export let loginRequest: { scopes: string[] } = { scopes: ['openid', 'profile', 'email'] };
export let apiRequest: { scopes: string[] } = { scopes: ['openid'] };
// Runtime-resolved API origin; empty string = same-origin behind the ingress.
export let apiBaseUrl: string | undefined;
export let echoProxyBaseUrl = '';
// Build metadata from /config.json (stamped into the image at build time); empty in local dev.
export let buildVersion = '';
export let buildCommit = '';

export async function initAuth(): Promise<void> {
  const runtime = (await fetchRuntimeConfig()) ?? {};
  applyRuntimeFeatures(runtime.enabledFeatures);
  const tenantId = runtime.entraTenantId || envTenantId;
  const clientId = runtime.adminClientId || envClientId;
  const authorityUrl = runtime.authorityUrl || envAuthorityUrl;
  apiBaseUrl = runtime.apiBaseUrl || (import.meta.env.VITE_API_BASE_URL ?? '');
  echoProxyBaseUrl = runtime.echoProxyBaseUrl || (import.meta.env.VITE_ECHOPROXY_BASE_URL ?? '');
  buildVersion = runtime.buildVersion ?? '';
  buildCommit = runtime.buildCommit ?? '';

  // EW-227: no dev bypass — a missing auth config is a hard error, not an open portal.
  if (!clientId) {
    throw new Error(
      'Auth is not configured: no admin client id (set VITE_AUTH_ADMIN_CLIENT_ID or the runtime adminClientId).',
    );
  }

  // A generic OIDC issuer gets NO api:// scope — measured on Keycloak, an undeclared
  // scope fails the login with invalid_scope; the audience comes from an IdP-side
  // audience mapper instead (same rule as the desktop's auth-oidc.mjs).
  apiScope = clientId && !authorityUrl ? `api://${clientId}/access_admin_api` : '';

  const msalConfig: Configuration = {
    auth: {
      clientId,
      redirectUri: window.location.origin + '/auth/callback',
      postLogoutRedirectUri: window.location.origin,
      ...(authorityUrl
        ? {
            authority: authorityUrl,
            knownAuthorities: [new URL(authorityUrl).host],
            protocolMode: ProtocolMode.OIDC,
            authorityMetadata: JSON.stringify({
              issuer: authorityUrl,
              authorization_endpoint: `${authorityUrl}/protocol/openid-connect/auth`,
              token_endpoint: `${authorityUrl}/protocol/openid-connect/token`,
              userinfo_endpoint: `${authorityUrl}/protocol/openid-connect/userinfo`,
              end_session_endpoint: `${authorityUrl}/protocol/openid-connect/logout`,
            }),
          }
        : { authority: `https://login.microsoftonline.com/${tenantId}` }),
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };

  msalInstance = new PublicClientApplication(msalConfig);
  loginRequest = { scopes: ['openid', 'profile', 'email', ...(apiScope ? [apiScope] : [])] };
  apiRequest = { scopes: apiScope ? [apiScope] : ['openid'] };
}
